import React, { useReducer, useContext, useEffect, useRef, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { dishesReducer } from './dishesReducer';
import {
	IDishesByCustomerV2,
	IGetPaginatedDishesByCustomerV2,
	IDishesByRestaurantV2,
	IGetPaginatedDishesByRestaurantV2,
	SortByForPaginatedDishes,
	DirectionForPaginated,
	IItemForGetShortInfo,
} from 'types';
import { PRE_SELECTED_CUSTOMER_ID_FOR_MENU } from 'appConstants';
import { DishesActionTypes } from './dishesActions';
import {
	dishesInitialState,
	DishesContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './dishesContext';
import {
	getPaginatedDishesByCustomerV2,
	getPaginatedDishesByRestaurantV2,
	getDishPhotos,
	deleteDishV2,
} from 'backend/dishes';
import { createAppError } from 'utils/helpers';
import { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

export const DishesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(dishesReducer, dishesInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const {
		refetch: refetchDishesByCustomer,
		response: responseDishesByCustomer,
		loading: loadingDishesByCustomer,
	} = useFetchData<AxiosResponse<IDishesByCustomerV2>, IGetPaginatedDishesByCustomerV2>(
		isMounted.current && state.IDSelectedCustomer && !state.IDSelectedRestaurant
			? getPaginatedDishesByCustomerV2
			: null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchInput: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer || '',
			sorting: state.sortBy
				? {
						sortBy: {
							'DishesA-Z': SortByForPaginatedDishes.Name,
							'DishesZ-A': SortByForPaginatedDishes.Name,
						}[state.sortBy],
						direction: {
							'DishesA-Z': DirectionForPaginated.Ascending,
							'DishesZ-A': DirectionForPaginated.Descending,
						}[state.sortBy],
				  }
				: null,
		},
	);
	const {
		refetch: refetchDishesByRestaurant,
		response: responseDishesByRestaurant,
		loading: loadingDishesByRestaurant,
	} = useFetchData<AxiosResponse<IDishesByRestaurantV2>, IGetPaginatedDishesByRestaurantV2>(
		isMounted.current && state.IDSelectedRestaurant ? getPaginatedDishesByRestaurantV2 : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchInput: state.searchString ? state.searchString : null,
			restaurantId: state.IDSelectedRestaurant || '',
			categoryId: state.IDSelectedCategory,
			subCategoryId: state.IDSelectedSubcategory,
			isDisabled: state.isDisabled,
			assigned: state.assigned ? true : null,
			sorting: state.sortBy
				? {
						sortBy: {
							'DishesA-Z': SortByForPaginatedDishes.Name,
							'DishesZ-A': SortByForPaginatedDishes.Name,
						}[state.sortBy],
						direction: {
							'DishesA-Z': DirectionForPaginated.Ascending,
							'DishesZ-A': DirectionForPaginated.Descending,
						}[state.sortBy],
				  }
				: null,
		},
	);

	useEffect(() => {
		loadingDishesByCustomer || loadingDishesByRestaurant ? showLoader() : hideLoader();
	}, [loadingDishesByCustomer, loadingDishesByRestaurant]);

	useEffect(() => {
		if (responseDishesByCustomer) {
			dispatch({
				type: DishesActionTypes.SET_DISHES_BY_CUSTOMER,
				payload: {
					dishesByCustomer: responseDishesByCustomer.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);

			const getDishesPreSignedUrl = async (dishIds: string[]) => {
				try {
					const dishesPhotosResponse = await getDishPhotos({ ids: dishIds, size: 1 });
					const updatedDishesByCustomer = responseDishesByCustomer.data.items.reduce(
						(acc: IDishesByCustomerV2['items'], item) => {
							const dishesPhotoFile = dishesPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: dishesPhotoFile ? dishesPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: DishesActionTypes.SET_DISHES_BY_CUSTOMER,
						payload: {
							dishesByCustomer: {
								totalCount: responseDishesByCustomer.data.totalCount,
								items: updatedDishesByCustomer,
							},
							typeOfModalWindow: state.typeOfModalWindow,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const dishIds = responseDishesByCustomer.data.items.map((dish) => dish.id);
			getDishesPreSignedUrl(dishIds);
		}
	}, [responseDishesByCustomer]);

	useEffect(() => {
		if (responseDishesByRestaurant) {
			dispatch({
				type: DishesActionTypes.SET_DISHES_BY_RESTAURANT,
				payload: {
					dishesByRestaurant: responseDishesByRestaurant.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);

			const getDishesPreSignedUrl = async (dishIds: string[]) => {
				try {
					const dishesPhotosResponse = await getDishPhotos({ ids: dishIds, size: 1 });
					const updatedDishesByRestaurant = responseDishesByRestaurant.data.items.reduce(
						(acc: IDishesByRestaurantV2['items'], item) => {
							const dishesPhotoFile = dishesPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: dishesPhotoFile ? dishesPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: DishesActionTypes.SET_DISHES_BY_RESTAURANT,
						payload: {
							dishesByRestaurant: {
								totalCount: responseDishesByRestaurant.data.totalCount,
								items: updatedDishesByRestaurant,
							},
							typeOfModalWindow: state.typeOfModalWindow,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const dishIds = responseDishesByRestaurant.data.items.map((dish) => dish.id);
			getDishesPreSignedUrl(dishIds);
		}
	}, [responseDishesByRestaurant]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetchDishesByCustomer();
			refetchDishesByRestaurant();
			dispatch({
				type: DishesActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	const refetchData = (value: boolean) => {
		dispatch({
			type: DishesActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	};

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteDishV2(state.IDForDeletion);

			if (state.dishesByCustomer.totalCount) {
				const filteredDishesByCustomer = state.dishesByCustomer.items.filter(
					(el) => el.id !== state.IDForDeletion,
				);
				dispatch({
					type: DishesActionTypes.SET_DISHES_BY_CUSTOMER,
					payload: {
						dishesByCustomer: {
							totalCount: state.dishesByCustomer.totalCount - 1,
							items: filteredDishesByCustomer,
						},
						typeOfModalWindow: 'success',
					},
				});
			}

			if (state.dishesByRestaurant.totalCount) {
				const filteredDishesByRestaurant = state.dishesByRestaurant.items.filter(
					(el) => el.id !== state.IDForDeletion,
				);
				dispatch({
					type: DishesActionTypes.SET_DISHES_BY_RESTAURANT,
					payload: {
						dishesByRestaurant: {
							totalCount: state.dishesByRestaurant.totalCount - 1,
							items: filteredDishesByRestaurant,
						},
						typeOfModalWindow: 'success',
					},
				});
			}
		} catch (err) {
			dispatch({
				type: DishesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: DishesActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const toggleRememberMyChoice = () => {
		!state.rememberMyChoice && state.IDSelectedCustomer
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, state.IDSelectedCustomer)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: DishesActionTypes.TOGGLE_REMEMBER_MY_CHOICE,
			payload: {
				rememberMyChoice: !state.rememberMyChoice,
			},
		});
	};

	const setFetchedCustomers = (value: IItemForGetShortInfo[]) => {
		dispatch({
			type: DishesActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	};

	const setIDCustomer = (value: Nullable<string>) => {
		value && state.rememberMyChoice
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: DishesActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
				dishesByCustomer: value ? state.dishesByCustomer : { totalCount: 0, items: [] },
				dishesByRestaurant: value ? state.dishesByRestaurant : { totalCount: 0, items: [] },
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: DishesActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
				isDisabled: value ? state.isDisabled : null,
				assigned: value ? state.assigned : false,
			},
		});
	};

	const setIDCategory = (value: Nullable<string>) => {
		dispatch({
			type: DishesActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const setIDSubcategory = (value: Nullable<string>) => {
		dispatch({
			type: DishesActionTypes.SET_ID_SUBCATEGORY,
			payload: {
				IDSelectedSubcategory: value,
			},
		});
	};

	const toggleIsDisabled = () => {
		let newIsDisabled;
		if (state.isDisabled === null) {
			newIsDisabled = false;
		} else if (state.isDisabled === false) {
			newIsDisabled = true;
		} else {
			newIsDisabled = null;
		}

		dispatch({
			type: DishesActionTypes.TOGGLE_IS_DISABLED,
			payload: {
				isDisabled: newIsDisabled,
			},
		});
	};

	const toggleAssigned = () => {
		dispatch({ type: DishesActionTypes.TOGGLE_ASSIGNED });
	};

	const resetSelectedControls = () => dispatch({ type: DishesActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: DishesActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: DishesActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: DishesActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: DishesActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForAssignRestaurants = (value: string) => {
		dispatch({
			type: DishesActionTypes.SET_ID_FOR_ASSIGN_RESTAURANTS,
			payload: {
				IDForAssignRestaurants: value,
				typeOfModalWindow: value ? 'assignRestaurants' : null,
			},
		});
	};

	const setIDForEditModGroupTemplate = (value: string) => {
		dispatch({
			type: DishesActionTypes.SET_ID_FOR_EDIT_MOD_GROUP_TEMPLATE,
			payload: {
				IDForEditModGroupTemplate: value,
				typeOfModalWindow: value ? 'editModGroupTemplate' : null,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: DishesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		!isMounted.current &&
			value.IDSelectedCustomer &&
			localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value.IDSelectedCustomer);

		dispatch({
			type: DishesActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: {
					...value,
					IDSelectedCustomer:
						value.IDSelectedCustomer || localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU),
				},
			},
		});
		isMounted.current = true;
		refetchDishesByCustomer();
		refetchDishesByRestaurant();
	};

	return (
		<DishesContext.Provider
			value={{
				refetchData,
				deleteSelectedItem,
				changeSearchString,
				toggleRememberMyChoice,
				setFetchedCustomers,
				setIDCustomer,
				setIDRestaurant,
				setIDCategory,
				setIDSubcategory,
				toggleIsDisabled,
				toggleAssigned,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForAssignRestaurants,
				setIDForEditModGroupTemplate,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				needRefetchData: state.needRefetchData,
				dishesByCustomer: state.dishesByCustomer,
				dishesByRestaurant: state.dishesByRestaurant,
				searchString: state.searchString,
				rememberMyChoice: state.rememberMyChoice,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				IDSelectedSubcategory: state.IDSelectedSubcategory,
				isDisabled: state.isDisabled,
				assigned: state.assigned,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForAssignRestaurants: state.IDForAssignRestaurants,
				IDForEditModGroupTemplate: state.IDForEditModGroupTemplate,
				isInitializedSelectCustomerModal: state.isInitializedSelectCustomerModal,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</DishesContext.Provider>
	);
};
